import { Map, List } from 'immutable'
import { createSelector } from 'reselect'
import { currentUser } from 'app/modules/user/selectors'
import { makeEndUserForConversationId } from 'app/modules/endUser/selectors'
import * as SocketSelectors from 'app/modules/socket/selectors'
import * as SocketConstants from 'app/modules/socket/constants'
import { UserTypes } from 'utils/UserTypes'
import { isBackendGenerated, isFirstBotInteractionMessage, isEmptyGoalMessage } from './helpers'
import * as constants from './constants'
import * as actions from './actions'

const root = (state) => state.get('messages')
const paginatedStatus = (state) => root(state).get('paginatedStatus')
const firstMessagesStatus = (state) => root(state).get('firstMessagesStatus')

const getMessagesByConversationId = (state) => root(state).getIn(['paginatedConversationMessages', 'messagesByConversationId'])

const getMessages = (state, props) => getMessagesByConversationId(state).get(props.conversationId, List())

export const currentSelectedMessageCount = (state) => root(state).get('messageToDisplay')
function extractPreMessages(message) {
  if (!message.hasIn(['attributes', 'preMessages'])) {
    return List.of(message)
  }

  return message.getIn(['attributes', 'preMessages'])
    .map((preMessage, index) => {
      const widgetGuid = `${message.getIn(['attributes', 'widgetGuid'])}-${index}`
      return message.withMutations(_message => {
        return _message
          .set('id', widgetGuid)
          .set('attributes', Map({ widgetGuid }))
          .set('body', preMessage.get('body'))
          .set('sender', preMessage.get('sender'))
          .set('authorId', preMessage.getIn(['sender', 'id']))
          .set('authorType', 'USER')
          .set('context', Map())
          .set('attachments', List())
          .set('isPreMessage', true)
      })
    })
    .push(message)
}
export const attachmentIdsForConversation = (conversationId) => (state) => {
  return getMessages(state, { conversationId }).flatMap(m => m.attachments)
}

export const firstMessagesForConversationId = (state, props) => root(state).getIn(['paginatedConversationMessages', 'firstMessagesById', props.conversationId], Map())

const editMessagesById = (state, props) =>
  root(state).getIn(['paginatedConversationMessages', 'editMessagesById', props.conversationId], Map())

export const makeConversationMessages = () => createSelector(
  [getMessages],
  (messages) => {
    return messages
        .filterNot(message => isBackendGenerated(message) || isFirstBotInteractionMessage(message) || isEmptyGoalMessage(message))
        .flatMap(extractPreMessages)
  })

const paginationById = (state) => root(state).getIn(['paginatedConversationMessages', 'paginationById'])

export const paginationHasMoreMessages = (state, props) => paginationById(state).getIn([props.conversationId, 'more'], false)
export const getNextPageId = (state, props) => paginationById(state).getIn([props.conversationId, 'next'], null)

export const firstChatMessage = (messages = makeConversationMessages()) => createSelector(
  messages,
  messages =>
    messages
      .filter(message => message.type === 'CHAT' && message.isPreMessage !== true)
      .first())

export const startInteractionMessage = createSelector(
  [firstMessagesForConversationId],
  (firstMessagesForConversationId) => firstMessagesForConversationId.filter(message => message.get('attributes', Map()).has('isFromConversationalLandingPage')).first()
)

export const firstEndUserMessage = () => createSelector(
  [firstMessagesForConversationId],
  (firstMessagesForConversationId) => {
    return firstMessagesForConversationId
      .filter(message => (['END_USER', 'LEAD'].includes(message.authorType)
        && message.type == "CHAT")).first()
  })

export const lastEndUserMessage = (messages = makeConversationMessages()) => createSelector(
    messages,
    messages =>
      messages
        .filter(message => (['END_USER', 'LEAD'].includes(message.authorType)
          && message.type == "CHAT")).last())

export const readReceipts = (state) => root(state).get('readReceipts', Map())

export const makeLastMessageIdReadByEndUser = (messages = makeConversationMessages(), endUser = makeEndUserForConversationId()) => createSelector(
  messages,
  endUser,
  readReceipts,
  (messages, endUser, receipts) => {
    const lastMessageUnreadByEndUser = endUser && (messages.findLast(message => {
      return message.type === 'CHAT' &&
             message.authorType === UserTypes.USER &&
             message.contentType !== 'PRIVATE_NOTE' &&
             receipts.getIn([message.id, endUser.type, endUser.id]) === constants.MessageStatus.Read
    }))

    return lastMessageUnreadByEndUser && lastMessageUnreadByEndUser.id
  }
)

export const makeLatestEditMessageSelector = () => {
  return createSelector(
    [
      editMessagesById,
      (state, props) => props
    ],
    (conversationMessagesForId, props) => conversationMessagesForId && conversationMessagesForId
      .sortBy(message => message.createdAt)
      .findLast((message) => message.get('type') === 'EDIT' && message.get('attributes') && message.get('attributes').get('editedMessageId') === props.messageId))
}

export const makeFirstMessageIdUnreadByAgent = (messages = makeConversationMessages()) => createSelector(
  messages,
  currentUser,
  readReceipts,
  (messages, currentUser, receipts) => {
    const index = currentUser && messages.findLastIndex(message => {
      return message.type === 'CHAT' &&
             receipts.getIn([message.id, UserTypes.USER, currentUser.id]) === constants.MessageStatus.Read
    })

    const firstUnreadMessage = messages.slice(index + 1).find(message => message.type === 'CHAT')

    if (index === -1 || !firstUnreadMessage) {
      return null
    }

    return firstUnreadMessage.id
  }
)

export const isReadByCurrentUser = (messageId) => createSelector(currentUser, readReceipts,
  (currentUser, receipts) => {
    return receipts.getIn([messageId, UserTypes.USER, currentUser.id]) === constants.MessageStatus.Read
  }
)

export const firstMessagesLoading = (state, props) => {
  return firstMessagesStatus(state).get(props.conversationId) === actions.fetchFirstMessagesPending.toString()
}

export const firstMessagesLoaded = (state, props) => {
  return firstMessagesStatus(state).get(props.conversationId) === actions.receiveFirstMessages.toString()
}

export const firstMessagesFailed = (state, props) => {
  return firstMessagesStatus(state).get(props.conversationId) === actions.fetchFirstMessagesFailed.toString()
}

export const firstPaginatedListLoaded = (state, props) => {
  return paginatedStatus(state).getIn([props.conversationId, 'firstFetch']) === actions.receivePaginatedMessageList.toString()
}

export const nextListPendingForConversation = (state, props) => {
  return paginatedStatus(state).getIn([props.conversationId, 'paginateFetch']) === actions.fetchNextPaginatedConversationMessagesPending.toString()
}

export const isUnableToConnect = (state) => {
  const clusterName = SocketConstants.SocketCluster.MESSAGES
  return SocketSelectors.didAuthenticationFail(state, { clusterName })
}

export const isAwaitingReconnect = (state) => {
  const clusterName = SocketConstants.SocketCluster.MESSAGES
  return !!SocketSelectors.channelsToJoin(state, { clusterName }).count()
}

export const timeUntilReconnect = (state) => {
  return SocketSelectors.getTimeUntilReconnect(state, {
    clusterName: SocketConstants.SocketCluster.MESSAGES
  })
}

export const latestPreviewableMessageFromAuthorByConversationId = (messages = makeConversationMessages()) =>
  createSelector([messages, (state, { authorId }) => authorId],
    (messages, authorId) => messages
      .filter(message => constants.TYPES_TO_PREVIEW.includes(message.type))
      .findLast(message => message.authorId === authorId))



// WEBPACK FOOTER //
// ./src/scripts/app/modules/message/selectors.js